import React, { useEffect, useState } from 'react';
import { InputNumber, Spin } from 'antd';
import { decodeHtmlEntities } from '../../pages/orderXV4Utils';

// Shared background/border/radius pair distinguishing the two source-of-truth
// sections shown per line item across StepReviewMatch.jsx and
// StepSummarySubmit.jsx's Detailed view: what the document said ("Extracted
// from Document", applied inside ExtractedFieldsBlock below) versus what it
// was matched to ("Matched Product", applied by each caller around its own
// Matched Product content, since that content's shape differs enough between
// the two callers that it isn't a shared component — see ExtractedFieldsBlock's
// own comment on that). Exported so both callers use identical colors instead
// of each guessing their own.
//
// `matched.unmatched` is a separate, neutral variant for the "Matched
// Product" box specifically when there's no catalog match -- the plain
// `matched` (success-green) style would otherwise sit around a "No catalog
// match found" message right next to the existing red danger border/tag for
// that same state, reading as a contradictory success/danger color clash.
export const CARD_STYLES = {
  extracted: { background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: 12 },
  matched: { background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: 12 },
  unmatched: { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 },
};

// `size` (default 84, matching the original StepReviewMatch card) lets a
// denser presentation (e.g. Summary & Submit's Detailed view table) use a
// smaller thumbnail without affecting existing callers that omit it.
export const ProductImage = ({ src, alt, size = 84 }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  if (!src || broken) {
    return (
      <div
        className="bg-light d-flex align-items-center justify-content-center rounded flex-shrink-0"
        style={{ width: size, height: size }}
      >
        <span className="text-muted small text-center px-1">No image</span>
      </div>
    );
  }
  return (
    <div
      className="bg-light d-flex align-items-center justify-content-center rounded flex-shrink-0 overflow-hidden"
      style={{ width: size, height: size }}
    >
      <img
        src={src}
        alt={alt}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        onError={() => setBroken(true)}
      />
    </div>
  );
};

// Renders the price comparison, UOM, Qty, and Extended Price as a small
// ecommerce-cart-style ledger, shared by any per-line-item card presentation
// in OrderXV4. The "From Document" row (the source document's own stated
// unit price) and the resolved-price row are shown side by side as
// equally-weighted rows — no burying the document price as small secondary
// text below the primary price — so the reviewer can compare them at a
// glance. A divergence `<Tag>` is rendered by the caller in its own badge
// row (see getPriceDivergenceBadge) rather than inside this panel.
//
// `unitPrice`/`extendedPrice`/`usingInputPriceFallback` are pre-resolved by
// the caller via resolveEffectiveLinePrice: per-tenant match pricing
// (match_json.pricing from order_genie_service.py's matching loop) > ERP/dummy
// bulk price > the source document's own stated price, used only when no real
// pricing engine is configured. The "From Document" row only renders when the
// source document actually provided a unit price (unchanged behavior for
// items with no input price).
//
// `readOnly` (default false) renders `qty` as plain text instead of the
// editable `<InputNumber>`, for read-only presentations where `onQtyChange`
// is not required to be called.
//
// `priceLabel` (default 'ERP Price') overrides the label on the `unitPrice`
// row. `unitPrice` is always the resolved/effective price, not necessarily
// the raw ERP-fetched price — a caller that separately shows the raw ERP
// price under its own "ERP Price" label elsewhere on the same screen (e.g.
// a table column) should pass a different label here to avoid two "ERP
// Price" labels meaning different numbers side by side.
export const PricingPanel = ({
  unitPrice, uom, qty, onQtyChange, extendedPrice,
  inputUnitPrice, inputExtendedPrice, usingInputPriceFallback, priceLoading, readOnly = false,
  priceLabel = 'ERP Price',
}) => {
  const hasInputUnitPrice = Number.isFinite(inputUnitPrice) && inputUnitPrice > 0;
  const hasInputExtendedPrice = Number.isFinite(inputExtendedPrice) && inputExtendedPrice > 0;
  const renderMoney = (value, suffix = `/${String(uom || '').trim() || 'ea'}`) => {
    if (priceLoading && !Number.isFinite(value)) {
      return <Spin size="small" />;
    }
    return Number.isFinite(value) ? `$${value.toFixed(2)}${suffix}` : '—';
  };
  return (
    <div className="border rounded p-2" style={{ minWidth: 190 }}>
      {/* Two separate blocks -- From Document, then {priceLabel} (usually ERP) --
          each showing its own unit price followed by its own extended price, so
          the two sources are never implied to be the same number. Order here
          (From Doc block, then ERP block; unit before extended within each)
          must match StepReviewMatch's Simple-view table cell. */}
      {hasInputUnitPrice && (
        <>
          <div className="text-muted small fw-semibold mb-1" style={{ letterSpacing: 0.3 }}>
            From Document
          </div>
          <div className="d-flex justify-content-between align-items-center small mb-1">
            <span className="text-muted">Price</span>
            <span className="fw-semibold">{`$${inputUnitPrice.toFixed(2)}/ea`}</span>
          </div>
          <div className="d-flex justify-content-between align-items-center small mb-2">
            <span className="text-muted">Extended</span>
            <span className="fw-semibold">
              {hasInputExtendedPrice ? `$${inputExtendedPrice.toFixed(2)}` : '—'}
            </span>
          </div>
          <hr className="my-1" />
        </>
      )}

      <div className="text-muted small fw-semibold mb-1" style={{ letterSpacing: 0.3 }}>
        {priceLabel}
      </div>
      <div className="d-flex justify-content-between align-items-center small mb-1">
        <span className="text-muted">Price</span>
        <span className="fw-semibold">{renderMoney(unitPrice)}</span>
      </div>
      <div className="d-flex justify-content-between align-items-center small mb-2">
        <span className="text-muted">Extended</span>
        <span className="fw-semibold" style={{ color: '#0f6b3f' }}>
          {renderMoney(extendedPrice, '')}
        </span>
      </div>

      <hr className="my-1" />
      <div className="d-flex justify-content-between align-items-center small mb-1">
        <span className="text-muted">UOM</span>
        <span>{uom || '—'}</span>
      </div>
      <div className="d-flex justify-content-between align-items-center small mb-2">
        <span className="text-muted">Qty</span>
        {readOnly ? (
          <span>{Number.isFinite(Number(qty)) ? qty : '—'}</span>
        ) : (
          <InputNumber
            size="small"
            min={1}
            precision={0}
            value={qty}
            onChange={(val) => onQtyChange(val || 1)}
            style={{ width: 80 }}
          />
        )}
      </div>
      {usingInputPriceFallback && (
        <div className="small text-muted" style={{ fontSize: 11 }}>
          <i className="bi bi-info-circle me-1" />Using price from input document — no pricing engine configured
        </div>
      )}
    </div>
  );
};

// Renders the "From Document" labeled block: the raw, per-line fields as
// extracted from the source document (part number, manufacturer, mfr part #,
// customer part #, description, notes) — distinct from whatever catalog
// product the line ends up matched to (the "Matched Product" block, rendered
// separately by each caller, since what counts as "matched" content differs
// enough between StepReviewMatch's editable card and StepSummarySubmit's
// read-only summary that a single shared component for it isn't a good fit).
//
// `partNumber`/`description` always render (even when blank, shown as "—")
// to preserve the pre-existing behavior of always showing those two; the
// four fields that were previously only visible via the "Edit Details" modal
// (manufacturer, manufacturerPartNumber, customerPartNumber, notes) are
// omitted entirely when blank, rather than showing an empty label row.
//
// Blank/decoded checks use the DECODED value (not the raw pre-decode string)
// so an entity-only value (e.g. "&nbsp;", which decodes to whitespace) is
// correctly treated as blank instead of passing a raw non-empty check and
// rendering a visibly-empty labeled line. Uses `!= null` rather than `||` so
// a legitimately falsy-but-real value like the string "0" is never dropped.
//
// Labeled "Extracted from Document" (not "From Document") to avoid sitting
// directly above PricingPanel's own "From Document" price row and reading as
// the same label meaning two different things on one card.
//
// Takes discrete field props (like `PricingPanel`) rather than a whole `item`
// object so both callers can pass through whatever shape they already have on
// hand — StepReviewMatch's `item.normalized_parts` and StepSummarySubmit's
// already-flattened `lineItems` entries — without either one reshaping data
// just to satisfy this component.
//
// `headerPrefix` (optional, default none) renders before the "Extracted from
// Document" heading, on the same row (e.g. StepReviewMatch's "LINE n"/"No
// match" tags, which previously sat in their own row above this block).
// `headerExtra` (optional, default none) renders after it, same row (e.g.
// StepReviewMatch's "Edit Details" button). Both let a caller collapse what
// would otherwise be separate rows into this one, without baking any
// caller-specific concept into this display-only component. `StepSummarySubmit`'s
// Detailed view omits both and is unaffected.
export const ExtractedFieldsBlock = ({
  partNumber, manufacturer, manufacturerPartNumber, customerPartNumber, description, notes,
  headerPrefix = null, headerExtra = null,
}) => {
  const buildFieldList = (defs) => defs
    .map((f) => ({ ...f, decoded: decodeHtmlEntities(f.value != null ? String(f.value) : '').trim() }))
    .filter((f) => f.always || f.decoded);

  // Part Number/Manufacturer/Mfr Part #/Customer Part # all pack into one
  // row per request. Description/Notes are free text meant to wrap, not
  // truncate, and stay on their own full-width rows below.
  const partNumberFields = buildFieldList([
    { label: 'Part Number', value: partNumber, always: true },
    { label: 'Manufacturer', value: manufacturer },
    { label: 'Mfr Part #', value: manufacturerPartNumber },
    { label: 'Customer Part #', value: customerPartNumber },
  ]);

  const freeTextFields = buildFieldList([
    { label: 'Description', value: description, always: true },
    { label: 'Notes', value: notes },
  ]);

  return (
    <div style={CARD_STYLES.extracted}>
      <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-1">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          {headerPrefix}
          <div className="text-muted small fw-semibold" style={{ letterSpacing: 0.3 }}>
            Extracted from Document
          </div>
        </div>
        {headerExtra}
      </div>
      {/* `minmax(0, 1fr)` (not bare `1fr`) is required for `text-truncate` to
          actually engage here — grid tracks otherwise default to a minimum
          size based on each item's min-content width, which for nowrap text
          means the track (and the grid) grows to fit the untruncated text
          instead of clipping it. */}
      <div
        className="small"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${partNumberFields.length}, minmax(0, 1fr))`,
          columnGap: 12,
        }}
      >
        {partNumberFields.map((f) => (
          <div className="text-truncate mb-1" key={f.label} title={f.decoded} aria-label={`${f.label}: ${f.decoded || 'not available'}`}>
            {`${f.label}: ${f.decoded || '—'}`}
          </div>
        ))}
      </div>
      {freeTextFields.map((f) => (
        <div className="small mb-1" key={f.label} aria-label={`${f.label}: ${f.decoded || 'not available'}`}>
          {`${f.label}: ${f.decoded || '—'}`}
        </div>
      ))}
    </div>
  );
};

// Dense, no-heading rendering of the same extracted fields ExtractedFieldsBlock
// shows, used by both StepReviewMatch's and StepSummarySubmit's Simple table
// view -- that view's column header already reads "Extracted from Document",
// and ExtractedFieldsBlock's own colored/padded box + repeated heading +
// full-word labels ("Part Number:", "Customer Part #:") cost far more vertical
// space than the values themselves, defeating the point of a dense view.
// Labels are abbreviated and long free-text fields are clamped to 2 lines
// (full text still available via the native `title` tooltip) so row height
// stays governed by the Matched Product column, not by whichever line item
// has the longest notes.
export const SimpleExtractedFields = ({
  partNumber, manufacturer, manufacturerPartNumber, customerPartNumber, description, notes,
}) => {
  const fields = [
    { label: 'Part #', value: partNumber },
    { label: 'Mfr', value: manufacturer },
    { label: 'Mfr Part #', value: manufacturerPartNumber },
    { label: 'Cust Part #', value: customerPartNumber },
    { label: 'Desc', value: description, clamp: true },
    { label: 'Notes', value: notes, clamp: true },
  ]
    .map((f) => ({ ...f, decoded: decodeHtmlEntities(f.value != null ? String(f.value) : '').trim() }))
    .filter((f) => f.decoded);
  if (!fields.length) return <span className="text-muted small">—</span>;
  return (
    <div className="small" style={{ lineHeight: 1.35 }}>
      {fields.map((f) => (
        <div
          key={f.label}
          title={f.decoded}
          style={f.clamp ? {
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          } : undefined}
        >
          <span className="text-muted">{f.label}:</span> {f.decoded}
        </div>
      ))}
    </div>
  );
};
